import { Suspense, lazy, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import type { RouteRecord } from 'vite-react-ssg';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AiTrafficTag } from '@/components/analytics/AiTrafficTag';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { InstallPrompt } from '@/components/notifications/InstallPrompt';
import { whenPageLoaded } from '@/lib/afterLoad';
import { recoverFromChunkFailure, purgeAppCaches } from '@/lib/chunkRecovery';
import { AuthProvider } from '@/context/AuthContext';
import { GuestProvider } from '@/context/GuestContext';
import { ChatWidgetProvider } from '@/context/ChatWidgetContext';
import { ChatWidgetFab } from '@/components/chat/ChatWidgetFab';
import { ChatWidgetOverlay } from '@/components/chat/ChatWidgetOverlay';
import { ErrorPage } from '@/pages/ErrorPage';
import { PRERENDER_PATHS, APP_SHELL_PATHS, PRERENDERED_PREFIXES, SHARE_SHELL_ID } from '@/routePaths';
import { LOCALES } from '@/lib/i18n';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 5, retry: 1 } },
});

/**
 * The push prompt is gated on being signed in and then waits three seconds
 * before it can appear, so nothing about it needs to exist during load. Split
 * out and mounted after `load`, it takes itself and the push-subscription
 * helpers out of the chunk every visitor downloads.
 *
 * InstallPrompt deliberately stays eager: it registers a `beforeinstallprompt`
 * listener, and Chrome frequently fires that before `load` — deferring it would
 * silently break the PWA install flow.
 */
const PushPrompt = lazy(() =>
  import('@/components/notifications/PushPrompt').then((m) => ({ default: m.PushPrompt })),
);

function DeferredPushPrompt() {
  const [afterLoad, setAfterLoad] = useState(false);
  useEffect(() => whenPageLoaded(() => setAfterLoad(true)), []);
  if (!afterLoad) return null;
  return (
    <Suspense fallback={null}>
      <PushPrompt />
    </Suspense>
  );
}

const PageLoader = () => (
  <div className="min-h-screen bg-gray-950 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Top-level providers wrapping the entire route tree. */
function AppProviders() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <GuestProvider>
          <ChatWidgetProvider>
            <AiTrafficTag />
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
            <DeferredPushPrompt />
            <InstallPrompt />
            <ChatWidgetFab />
            <ChatWidgetOverlay />
          </ChatWidgetProvider>
        </GuestProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Helper: react-router lazy expects a module exporting `Component`.
// A failed dynamic import almost always means a deploy rotated the chunk hashes
// under a page that was already open, so the URL it asks for no longer exists.
// `recoverFromChunkFailure` drops any stale service-worker cache and reloads
// once; a second failure is a real one and bubbles to the errorElement.
const page = (
  loader: () => Promise<Record<string, React.ComponentType>>,
  name: string,
) => async () => {
  try {
    const m = await loader();
    return { Component: m[name] };
  } catch (err) {
    if (typeof window !== 'undefined') {
      const recovered = await recoverFromChunkFailure({
        storage: sessionStorage,
        purge: purgeAppCaches,
        reload: () => window.location.reload(),
        now: () => Date.now(),
      });
      if (recovered) return new Promise<never>(() => {}); // page is reloading — never settle
    }
    throw err;
  }
};

export const routes: RouteRecord[] = [
  {
    element: <AppProviders />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <Layout />,
        children: [
          { index: true, lazy: page(() => import('@/pages/HomePage'), 'HomePage') },
          { path: 'chat', lazy: page(() => import('@/pages/ChatPage'), 'ChatPage') },
          { path: 'sign-in', lazy: page(() => import('@/pages/SignInPage'), 'SignInPage') },
          { path: 'sign-up', lazy: page(() => import('@/pages/SignUpPage'), 'SignUpPage') },
          {
            path: 'share/:shareId',
            lazy: page(() => import('@/pages/SharePage'), 'SharePage'),
            // Share ids are unbounded, so we prerender one shell and nginx
            // serves it for every /share/* URL. SharePage's first paint is
            // id-independent (a loading card), so this hydrates cleanly —
            // without it, a shared link served the prerendered HOMEPAGE.
            getStaticPaths: async () => [`/share/${SHARE_SHELL_ID}`],
          },
          { path: 'pricing', lazy: page(() => import('@/pages/PricingPage'), 'PricingPage') },
          { path: 'payment-status', lazy: page(() => import('@/pages/PaymentStatusPage'), 'PaymentStatusPage') },
          { path: 'about', lazy: page(() => import('@/pages/AboutPage'), 'AboutPage') },
          { path: 'accuracy', lazy: page(() => import('@/pages/AccuracyPage'), 'AccuracyPage') },
          { path: 'contact', lazy: page(() => import('@/pages/ContactPage'), 'ContactPage') },
          { path: 'privacy', lazy: page(() => import('@/pages/PrivacyPolicyPage'), 'PrivacyPolicyPage') },
          { path: 'terms', lazy: page(() => import('@/pages/TermsOfServicePage'), 'TermsOfServicePage') },
          { path: 'blog', lazy: page(() => import('@/pages/BlogListPage'), 'BlogListPage') },
          {
            path: 'blog/:slug',
            lazy: page(() => import('@/pages/BlogPostPage'), 'BlogPostPage'),
            // Provide the static slugs to prerender:
            getStaticPaths: async () => {
              const { getAllPosts } = await import('@/lib/blog');
              return getAllPosts().map((p) => `/blog/${p.slug}`);
            },
          },
          { path: 'astrology', lazy: page(() => import('@/pages/AstrologyHubPage'), 'AstrologyHubPage') },
          {
            path: 'astrology/:slug',
            lazy: page(() => import('@/pages/TopicPage'), 'TopicPage'),
            getStaticPaths: async () => {
              const { getAllTopics } = await import('@/content/topics');
              return getAllTopics().map((t) => `/astrology/${t.slug}`);
            },
          },
          // SEO tool/calculator landing pages (public, prerendered)
          { path: 'ai-astrologer', lazy: page(() => import('@/pages/ToolPage'), 'AiAstrologerPage') },
          { path: 'free-kundli', lazy: page(() => import('@/pages/ToolPage'), 'FreeKundliPage') },
          { path: 'kundli-matching', lazy: page(() => import('@/pages/ToolPage'), 'KundliMatchingPage') },
          { path: 'mangal-dosha-calculator', lazy: page(() => import('@/pages/ToolPage'), 'MangalDoshaCalculatorPage') },
          { path: 'nakshatra-calculator', lazy: page(() => import('@/pages/ToolPage'), 'NakshatraCalculatorPage') },
          { path: 'dasha-calculator', lazy: page(() => import('@/pages/ToolPage'), 'DashaCalculatorPage') },
          { path: 'lagna-calculator', lazy: page(() => import('@/pages/ToolPage'), 'LagnaCalculatorPage') },
          { path: 'tools', lazy: page(() => import('@/pages/ToolsHubPage'), 'ToolsHubPage') },
          // Daily panchang: a public page whose content changes every day, so
          // there's a reason to come back rather than visit once.
          { path: 'panchang', lazy: page(() => import('@/pages/PanchangPage'), 'PanchangPage') },
          // Daily rashifal: one hub plus twelve sign pages, all prerendered; the
          // day's values are fetched client-side like the panchang.
          { path: 'rashifal', lazy: page(() => import('@/pages/RashifalHubPage'), 'RashifalHubPage') },
          {
            path: 'rashifal/:sign',
            lazy: page(() => import('@/pages/RashifalSignPage'), 'RashifalSignPage'),
            getStaticPaths: async () => {
              const { SIGN_PROFILES } = await import('@/content/rashifal');
              return SIGN_PROFILES.map((s) => `/rashifal/${s.slug}`);
            },
          },
          // Reference pages (CLAUDE.md I3): the 27 nakshatras, English-only for now.
          { path: 'nakshatra', lazy: page(() => import('@/pages/NakshatraHubPage'), 'NakshatraHubPage') },
          {
            path: 'nakshatra/:slug',
            lazy: page(() => import('@/pages/NakshatraPage'), 'NakshatraPage'),
            getStaticPaths: async () => {
              const { NAKSHATRA_PROFILES } = await import('@/content/nakshatra/data');
              return NAKSHATRA_PROFILES.map((n) => `/nakshatra/${n.slug}`);
            },
          },
          { path: 'planet-in-house', lazy: page(() => import('@/pages/PlanetHouseHubPage'), 'PlanetHouseHubPage') },
          {
            path: 'planet-in-house/:slug',
            lazy: page(() => import('@/pages/PlanetHousePage'), 'PlanetHousePage'),
            getStaticPaths: async () => {
              const { PAIRS } = await import('@/content/planet-house/data');
              return PAIRS.map((p) => `/planet-in-house/${p.slug}`);
            },
          },
          { path: 'lagna', lazy: page(() => import('@/pages/LagnaHubPage'), 'LagnaHubPage') },
          {
            path: 'lagna/:slug',
            lazy: page(() => import('@/pages/LagnaPage'), 'LagnaPage'),
            getStaticPaths: async () => {
              const { LAGNA_PROFILES } = await import('@/content/lagna/data');
              return LAGNA_PROFILES.map((l) => `/lagna/${l.slug}`);
            },
          },
          { path: 'yoga', lazy: page(() => import('@/pages/YogaHubPage'), 'YogaHubPage') },
          {
            path: 'yoga/:slug',
            lazy: page(() => import('@/pages/YogaPage'), 'YogaPage'),
            getStaticPaths: async () => {
              const { YOGA_CATALOG } = await import('@kundaliai/shared');
              return YOGA_CATALOG.map((y) => `/yoga/${y.slug}`);
            },
          },
          // Localised daily pages: /hi/rashifal, /hi/rashifal/mesh, /hi/panchang, and
          // the same under every other prefix in locales.json. The pages read the
          // locale from the URL, so these are the same components.
          ...LOCALES.filter((l) => l.prefix).flatMap((l) => {
            const prefix = l.prefix.slice(1);
            return [
              { path: `${prefix}/rashifal`, lazy: page(() => import('@/pages/RashifalHubPage'), 'RashifalHubPage') },
              {
                path: `${prefix}/rashifal/:sign`,
                lazy: page(() => import('@/pages/RashifalSignPage'), 'RashifalSignPage'),
                getStaticPaths: async () => Object.values(l.signSlugs).map((slug) => `${l.prefix}/rashifal/${slug}`),
              },
              { path: `${prefix}/panchang`, lazy: page(() => import('@/pages/PanchangPage'), 'PanchangPage') },
              // Full site for site-enabled locales (locales.json `site: true`).
              ...(l.site
                ? [
                    { path: prefix, lazy: page(() => import('@/pages/HomePage'), 'HomePage') },
                    { path: `${prefix}/tools`, lazy: page(() => import('@/pages/ToolsHubPage'), 'ToolsHubPage') },
                    { path: `${prefix}/about`, lazy: page(() => import('@/pages/AboutPage'), 'AboutPage') },
                    { path: `${prefix}/contact`, lazy: page(() => import('@/pages/ContactPage'), 'ContactPage') },
                    { path: `${prefix}/pricing`, lazy: page(() => import('@/pages/PricingPage'), 'PricingPage') },
                    { path: `${prefix}/astrology`, lazy: page(() => import('@/pages/AstrologyHubPage'), 'AstrologyHubPage') },
                    {
                      path: `${prefix}/astrology/:slug`,
                      lazy: page(() => import('@/pages/TopicPage'), 'TopicPage'),
                      getStaticPaths: async () => {
                        const { getAllTopics } = await import('@/content/topics');
                        return getAllTopics().map((t) => `${l.prefix}/astrology/${t.slug}`);
                      },
                    },
                    { path: `${prefix}/ai-astrologer`, lazy: page(() => import('@/pages/ToolPage'), 'AiAstrologerPage') },
                    { path: `${prefix}/free-kundli`, lazy: page(() => import('@/pages/ToolPage'), 'FreeKundliPage') },
                    { path: `${prefix}/kundli-matching`, lazy: page(() => import('@/pages/ToolPage'), 'KundliMatchingPage') },
                    { path: `${prefix}/mangal-dosha-calculator`, lazy: page(() => import('@/pages/ToolPage'), 'MangalDoshaCalculatorPage') },
                    { path: `${prefix}/nakshatra-calculator`, lazy: page(() => import('@/pages/ToolPage'), 'NakshatraCalculatorPage') },
                    { path: `${prefix}/dasha-calculator`, lazy: page(() => import('@/pages/ToolPage'), 'DashaCalculatorPage') },
                    { path: `${prefix}/lagna-calculator`, lazy: page(() => import('@/pages/ToolPage'), 'LagnaCalculatorPage') },
                  ]
                : []),
            ];
          }),
          // /404 is the prerendered page nginx serves via error_page; '*' is what
          // the client renders for the same URL, so the two must be identical
          // markup or hydration would mismatch.
          { path: '404', lazy: page(() => import('@/pages/NotFoundPage'), 'NotFoundPage') },
          {
            element: <ProtectedRoute />,
            children: [
              { path: 'dashboard', lazy: page(() => import('@/pages/DashboardPage'), 'DashboardPage') },
              { path: 'profile', lazy: page(() => import('@/pages/ProfilePage'), 'ProfilePage') },
              { path: 'kundali', lazy: page(() => import('@/pages/KundaliPage'), 'KundaliPage') },
              { path: 'kundali/:id', lazy: page(() => import('@/pages/KundaliPage'), 'KundaliPage') },
              { path: 'predictions', lazy: page(() => import('@/pages/PredictionsPage'), 'PredictionsPage') },
              { path: 'reports', lazy: page(() => import('@/pages/ReportsPage'), 'ReportsPage') },
              { path: 'compatibility', lazy: page(() => import('@/pages/CompatibilityPage'), 'CompatibilityPage') },
              { path: 'dosha', lazy: page(() => import('@/pages/DoshaPage'), 'DoshaPage') },
              { path: 'remedies', lazy: page(() => import('@/pages/RemediesPage'), 'RemediesPage') },
            ],
          },
          { path: '*', lazy: page(() => import('@/pages/NotFoundPage'), 'NotFoundPage') },
        ],
      },
    ],
  },
];

/**
 * Filter passed to vite-react-ssg to restrict prerendering to public routes.
 * Exported from the SSG entry (main.tsx) — vite-react-ssg reads `includedRoutes`
 * as a named export of the server entry (see vite-react-ssg build pipeline).
 * Everything not matched here is left to the client-rendered SPA fallback.
 */
export function includedRoutes(paths: string[]): string[] {
  const allowedExact = new Set([...PRERENDER_PATHS, ...APP_SHELL_PATHS]);
  // vite-react-ssg collects nested route paths without a leading slash
  // (e.g. 'about'), while getStaticPaths yields absolute paths ('/blog/foo').
  // Normalize to a leading-slash form before matching against PRERENDER_PATHS.
  const norm = (p: string) => (p === '/' ? '/' : `/${p.replace(/^\//, '')}`);
  return paths
    .map(norm)
    .filter((n) => allowedExact.has(n) || PRERENDERED_PREFIXES.some((prefix) => n.startsWith(prefix)));
}

export { PRERENDER_PATHS, APP_SHELL_PATHS };
